import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { Marca, MarcaCreate, MarcaUpdate, Laboratorio } from '@/modules/multichannel/types/configuracion'

interface MarcaFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  marca: Marca | null
  laboratorios: Laboratorio[]
  onSubmit: (data: MarcaCreate) => void
  onUpdate: (id: number, data: MarcaUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  marca_principal_id: string
  nombre: string
  descripcion: string
}

export function MarcaFormDialog({
  open, onOpenChange, marca, laboratorios, onSubmit, onUpdate, isSubmitting,
}: MarcaFormDialogProps) {
  const isEdit = !!marca

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { marca_principal_id: '', nombre: '', descripcion: '' },
  })

  useEffect(() => {
    if (open && marca) {
      reset({
        marca_principal_id: String(marca.marca_principal_id),
        nombre: marca.nombre,
        descripcion: marca.descripcion || '',
      })
    } else if (!open) {
      reset({ marca_principal_id: '', nombre: '', descripcion: '' })
    }
  }, [open, marca, reset])

  const onFormSubmit = (data: FormData) => {
    if (isEdit && marca) {
      onUpdate(marca.id, {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        marca_principal_id: Number(data.marca_principal_id),
      })
    } else {
      onSubmit({
        marca_principal_id: Number(data.marca_principal_id),
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
      })
    }
  }

  const activeLabs = laboratorios.filter(l => l.activo)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifica los datos de la marca.' : 'Asocia la marca a un laboratorio.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="marca_principal_id">Laboratorio <span className="text-destructive">*</span></Label>
            <select
              id="marca_principal_id"
              {...register('marca_principal_id', { required: 'Selecciona un laboratorio' })}
              disabled={isSubmitting}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Seleccionar laboratorio...</option>
              {activeLabs.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
            {errors.marca_principal_id && <p className="text-sm text-destructive">{errors.marca_principal_id.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre <span className="text-destructive">*</span></Label>
            <Input id="nombre" {...register('nombre', { required: 'El nombre es requerido' })} disabled={isSubmitting} />
            {errors.nombre && <p className="text-sm text-destructive">{errors.nombre.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <Input id="descripcion" {...register('descripcion')} disabled={isSubmitting} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
